import { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faKey,
  faInfinity,
  faChartLine,
  faBan,
  faPlus,
  faTrash,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { apiKeysAdminApi } from '../../../services/admin/apiKeysAdminApi'
import type {
  AdminApiKey,
  AdminApiKeyRequest,
  AdminKeyStatusFilter,
} from '../../../types/apiKeyAdmin'
import type { ApiKeyCreateResponse } from '../../../types/apiKeys'
import ApiKeyUsagePanel from './ApiKeyUsagePanel'
import IssueKeyDialog from './IssueKeyDialog'
import IssuedKeyReveal from './IssuedKeyReveal'

const FILTERS: { value: AdminKeyStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'perpetual', label: 'Perpetual' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'deleted', label: 'Deleted' },
]

/**
 * The request queue is a seventh pill rather than a separate page.
 *
 * Nothing else tells an admin a request is waiting — there is no email and no
 * notification — so the count has to live where they already come to do key
 * work, not behind a nav item they might not visit.
 */
const REQUESTS_VIEW = 'requests' as const
type View = AdminKeyStatusFilter | typeof REQUESTS_VIEW

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function StatusCell({ apiKey }: { apiKey: AdminApiKey }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-gray-200 text-gray-700',
    revoked: 'bg-red-100 text-red-800',
    deleted: 'bg-gray-100 text-gray-500 line-through',
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${
          styles[apiKey.status] ?? styles.expired
        }`}
      >
        {apiKey.status}
      </span>
      {apiKey.never_expires && apiKey.status === 'active' && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800"
          title={apiKey.justification ?? 'Never-expiring key'}
        >
          <FontAwesomeIcon icon={faInfinity} className="h-3 w-3" />
          Perpetual
        </span>
      )}
    </div>
  )
}

/**
 * The perpetual-key request queue.
 *
 * Shows the justification in full rather than truncated: it is the entire basis
 * for the decision, and a queue that hides it invites approving on the label
 * alone. Oldest first, because this is work to get through.
 */
function RequestsTable({
  requests,
  loading,
  decidingId,
  onApprove,
  onDeny,
}: {
  requests: AdminApiKeyRequest[]
  loading: boolean
  decidingId: string | null
  onApprove: (request: AdminApiKeyRequest) => void
  onDeny: (request: AdminApiKeyRequest) => void
}) {
  const statusStyles: Record<string, string> = {
    pending: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    denied: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-gray-200 text-gray-600',
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {['Account', 'Label', 'Why it cannot rotate', 'Permissions', 'Status', 'Requested', ''].map(
              (h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {h}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {loading && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                Loading…
              </td>
            </tr>
          )}
          {!loading && requests.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                No one has requested a non-expiring key.
              </td>
            </tr>
          )}
          {!loading &&
            requests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">
                  {request.requester_email ?? (
                    <span className="font-mono text-xs text-gray-500">
                      {request.supabase_user_id}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{request.label ?? '—'}</td>
                <td className="max-w-md px-4 py-3 text-sm text-gray-700">
                  {request.justification}
                  {request.status === 'denied' && request.denial_reason && (
                    <p className="mt-1 text-xs text-amber-700">
                      Declined: {request.denial_reason}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {request.permission === 'read_only' ? 'Read only' : 'Read & write'}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${
                      statusStyles[request.status] ?? statusStyles.cancelled
                    }`}
                  >
                    {request.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {formatDate(request.requested_at)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => onApprove(request)}
                        disabled={decidingId === request.id}
                        className="mr-3 inline-flex items-center text-sm font-medium text-green-700 hover:text-green-900 disabled:text-gray-400"
                      >
                        <FontAwesomeIcon icon={faInfinity} className="mr-1 h-4 w-4" />
                        {decidingId === request.id ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        onClick={() => onDeny(request)}
                        disabled={decidingId === request.id}
                        className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                      >
                        <FontAwesomeIcon icon={faBan} className="mr-1 h-4 w-4" />
                        Decline
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Global view of every partner API key.
 *
 * Perpetual keys are badged rather than hidden in a filter — they are the ones
 * with no automatic expiry to contain a leak, so they should be the first thing
 * an admin's eye lands on when scanning this table.
 */
export default function ApiKeyList() {
  const [keys, setKeys] = useState<AdminApiKey[]>([])
  const [filter, setFilter] = useState<View>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AdminApiKey | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [issuing, setIssuing] = useState(false)
  const [search, setSearch] = useState('')

  const [requests, setRequests] = useState<AdminApiKeyRequest[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [decidingId, setDecidingId] = useState<string | null>(null)
  const [approved, setApproved] = useState<{
    created: ApiKeyCreateResponse
    recipient: string | null
  } | null>(null)

  const showingRequests = filter === REQUESTS_VIEW

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (showingRequests) {
        const result = await apiKeysAdminApi.requests.list('all')
        setRequests(result.requests)
        setPendingCount(result.pending_count)
      } else {
        const result = await apiKeysAdminApi.list(filter as AdminKeyStatusFilter, search)
        setKeys(result.api_keys)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [filter, search, showingRequests])

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  // The badge has to be right on arrival, before anyone opens the queue, so the
  // count is fetched independently of the current view.
  useEffect(() => {
    apiKeysAdminApi.requests
      .list('pending')
      .then((result) => setPendingCount(result.pending_count))
      .catch(() => {
        /* The badge is not worth an error banner over. */
      })
  }, [])

  const handleApprove = async (request: AdminApiKeyRequest) => {
    const who = request.requester_email ?? request.supabase_user_id
    if (
      !window.confirm(
        `Issue a NEVER-EXPIRING key to ${who}?\n\n` +
          `Reason given: ${request.justification}\n\n` +
          'A perpetual key has no automatic containment — if it leaks, revoking it by ' +
          'hand is the only remedy.'
      )
    ) {
      return
    }

    setDecidingId(request.id)
    setError(null)
    try {
      const created = await apiKeysAdminApi.requests.approve(request.id)
      setApproved({ created, recipient: request.requester_email })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request')
    } finally {
      setDecidingId(null)
    }
  }

  const handleDeny = async (request: AdminApiKeyRequest) => {
    // Required by the API too — a refusal with no reason just produces the same
    // request again next week.
    const reason = window.prompt(
      `Why is this request being declined?\n\nThe requester will see this.`
    )
    if (reason === null) return
    if (!reason.trim()) {
      setError('A reason is required when declining a request.')
      return
    }

    setDecidingId(request.id)
    setError(null)
    try {
      await apiKeysAdminApi.requests.deny(request.id, reason.trim())
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline request')
    } finally {
      setDecidingId(null)
    }
  }

  const handleRevoke = async (apiKey: AdminApiKey) => {
    const owner = apiKey.owner_email ?? apiKey.supabase_user_id
    if (
      !window.confirm(
        `Revoke ${apiKey.preview} belonging to ${owner}?\n\nRequests using this key will fail immediately.`
      )
    ) {
      return
    }

    setRevokingId(apiKey.id)
    try {
      await apiKeysAdminApi.revoke(apiKey.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key')
    } finally {
      setRevokingId(null)
    }
  }

  const handleDelete = async (apiKey: AdminApiKey) => {
    const owner = apiKey.owner_email ?? apiKey.supabase_user_id
    if (
      !window.confirm(
        `Delete ${apiKey.preview} belonging to ${owner}?\n\n` +
          'It is hidden from this list and revoked, but the record and its usage ' +
          'history are kept. You can see it again with the Deleted filter.'
      )
    ) {
      return
    }

    setDeletingId(apiKey.id)
    try {
      await apiKeysAdminApi.remove(apiKey.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete API key')
    } finally {
      setDeletingId(null)
    }
  }

  const perpetualCount = keys.filter((k) => k.never_expires && k.status === 'active').length

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="flex items-center text-2xl font-bold text-gray-900">
          <FontAwesomeIcon icon={faKey} className="mr-2 h-6 w-6 text-blue-600" />
          API Keys
        </h1>
        <div className="flex items-start justify-between gap-4">
          <p className="mt-1 text-sm text-gray-600">
            Every API key across the platform. Select a key to review its traffic before
            revoking.
          </p>
          <button
            onClick={() => setIssuing(true)}
            className="shrink-0 inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
            Issue key
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              filter === f.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setFilter(REQUESTS_VIEW)}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium ${
            showingRequests
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Requests
          {pendingCount > 0 && (
            <span
              className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs font-semibold ${
                showingRequests ? 'bg-white text-blue-700' : 'bg-amber-100 text-amber-800'
              }`}
            >
              {pendingCount}
            </span>
          )}
        </button>
        {/* Search filters keys, not requests — offering it over the queue would
            imply a filter that does nothing. */}
        {!showingRequests && (
          <div className="relative ml-auto">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name"
              className="w-64 rounded-full border border-gray-300 py-1.5 pl-9 pr-3 text-sm"
            />
          </div>
        )}

        {!showingRequests && perpetualCount > 0 && (
          <span className="text-sm text-amber-700">
            {perpetualCount} perpetual key{perpetualCount === 1 ? '' : 's'} in circulation
          </span>
        )}
      </div>

      {showingRequests ? (
        <RequestsTable
          requests={requests}
          loading={loading}
          decidingId={decidingId}
          onApprove={handleApprove}
          onDeny={handleDeny}
        />
      ) : (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Account', 'Label', 'Key', 'Permissions', 'Status', 'Created', 'Expires', 'Last used', ''].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && keys.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  No keys match this filter.
                </td>
              </tr>
            )}
            {!loading &&
              keys.map((apiKey) => (
                <tr key={apiKey.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {apiKey.owner_email ?? (
                      <span className="font-mono text-xs text-gray-500">
                        {apiKey.supabase_user_id}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{apiKey.label ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{apiKey.preview}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {apiKey.permission === 'read_only' ? 'Read only' : 'Read & write'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusCell apiKey={apiKey} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(apiKey.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {apiKey.never_expires ? 'Never' : formatDate(apiKey.expires_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setSelected(apiKey)}
                      className="mr-3 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      <FontAwesomeIcon icon={faChartLine} className="mr-1 h-4 w-4" />
                      Activity
                    </button>
                    {apiKey.status !== 'revoked' && apiKey.status !== 'deleted' && (
                      <button
                        onClick={() => handleRevoke(apiKey)}
                        disabled={revokingId === apiKey.id}
                        className="mr-3 inline-flex items-center text-sm font-medium text-red-600 hover:text-red-800 disabled:text-gray-400"
                      >
                        <FontAwesomeIcon icon={faBan} className="mr-1 h-4 w-4" />
                        {revokingId === apiKey.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                    {apiKey.status !== 'deleted' && (
                      <button
                        onClick={() => handleDelete(apiKey)}
                        disabled={deletingId === apiKey.id}
                        className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                        title="Hide from this list and revoke. The record is kept."
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1 h-4 w-4" />
                        {deletingId === apiKey.id ? 'Deleting…' : 'Delete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}

      {issuing && (
        <IssueKeyDialog onClose={() => setIssuing(false)} onIssued={load} />
      )}

      {approved && (
        <IssuedKeyReveal
          created={approved.created}
          recipient={approved.recipient}
          onClose={() => setApproved(null)}
        />
      )}

      {selected && (
        <ApiKeyUsagePanel
          apiKey={selected}
          onClose={() => setSelected(null)}
          onRevoked={async () => {
            setSelected(null)
            await load()
          }}
        />
      )}
    </div>
  )
}
